import { createContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { createTheme, useMediaQuery } from '@mui/material'

const themeDark = createTheme({
  palette: {
    mode: 'dark',
    textPrimary: '#39ACE7',
    background: {
      main: '#26272aff',
      default: '#26272aff',
      paper: '#2a2b2eff'
    },
    primary: {
      main: '#00ccffff'
    }
  },
  isDark: true
})

const themeLight = createTheme({
  palette: {
    mode: 'light',
    textPrimary: '#00ccffff',
    background: {
      main: '#fff',
      default: '#fff',
      paper: '#fff'
    },
    primary: {
      main: '#00ccffff'
    }
  },
  isDark: false
})

// Tema preview — identidade Conversacional WhatsApp.
// Verde WhatsApp como acento, tons quentes neutros, tipografia humana,
// cantos arredondados (14px) e microanimações.
// Mantém isDark: true para compat com componentes que checam isDark
// (logos Meta, gradientes condicionais, etc).
const PREVIEW_BG = '#15171a'
const PREVIEW_SURFACE = '#1f2326'
const PREVIEW_SURFACE_HOVER = '#262a2e'
const PREVIEW_BORDER = 'rgba(255, 255, 255, 0.06)'
const PREVIEW_BORDER_STRONG = 'rgba(255, 255, 255, 0.12)'
const PREVIEW_PRIMARY = '#25d366'
const PREVIEW_PRIMARY_DARK = '#1ea854'
const PREVIEW_PRIMARY_LIGHT = '#4ade80'
const PREVIEW_TEXT_PRIMARY = '#f1f5f1'
const PREVIEW_TEXT_SECONDARY = '#9ca3a0'

const themePreview = createTheme({
  palette: {
    mode: 'dark',
    textPrimary: PREVIEW_PRIMARY,
    background: {
      main: PREVIEW_BG,
      default: PREVIEW_BG,
      paper: PREVIEW_SURFACE
    },
    primary: {
      main: PREVIEW_PRIMARY,
      light: PREVIEW_PRIMARY_LIGHT,
      dark: PREVIEW_PRIMARY_DARK,
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#34a5ff',
      light: '#60a5fa',
      dark: '#2563eb',
      contrastText: '#ffffff'
    },
    success: { main: '#22c55e' },
    warning: { main: '#f59e0b' },
    error: { main: '#ef4444' },
    info: { main: '#34a5ff' },
    divider: PREVIEW_BORDER,
    text: {
      primary: PREVIEW_TEXT_PRIMARY,
      secondary: PREVIEW_TEXT_SECONDARY
    }
  },
  shape: {
    borderRadius: 14
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", Roboto, "Helvetica", Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    body1: { letterSpacing: '0.005em' },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em'
    }
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: PREVIEW_SURFACE,
          border: `1px solid ${PREVIEW_BORDER}`,
          transition: 'border-color 200ms ease, transform 200ms ease, box-shadow 200ms ease'
        },
        elevation2: {
          boxShadow: '0 1px 2px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.20)'
        },
        elevation3: {
          boxShadow: '0 2px 6px rgba(0,0,0,0.22), 0 12px 32px rgba(0,0,0,0.24)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
          textTransform: 'none',
          letterSpacing: '0.01em',
          paddingTop: 8,
          paddingBottom: 8
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: `0 6px 18px ${PREVIEW_PRIMARY_DARK}40`,
            transform: 'translateY(-1px)'
          }
        },
        outlined: {
          borderColor: PREVIEW_BORDER_STRONG,
          '&:hover': { borderColor: PREVIEW_PRIMARY }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, letterSpacing: '0.02em', borderRadius: 999 }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0f1112',
          color: PREVIEW_TEXT_PRIMARY,
          fontSize: 12,
          padding: '10px 14px',
          borderRadius: 10,
          border: `1px solid ${PREVIEW_BORDER_STRONG}`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
        },
        arrow: { color: '#0f1112' }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: PREVIEW_BG,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: PREVIEW_BORDER_STRONG
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: `${PREVIEW_PRIMARY}80`
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: PREVIEW_PRIMARY,
            borderWidth: 2
          }
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 600 }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: PREVIEW_BG
        },
        '@keyframes preview-pop': {
          '0%':   { opacity: 0, transform: 'translateY(10px) scale(0.98)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' }
        },
        '@keyframes preview-pulse-soft': {
          '0%, 100%': { boxShadow: `0 0 0 0 ${PREVIEW_PRIMARY}66` },
          '50%':      { boxShadow: `0 0 0 8px ${PREVIEW_PRIMARY}00` }
        },
        '.preview-card-enter': {
          animation: 'preview-pop 480ms cubic-bezier(0.22, 1, 0.36, 1) both'
        },
        '.preview-pulse': {
          animation: 'preview-pulse-soft 2s infinite'
        }
      }
    }
  },
  isDark: true,
  isPreview: true,
  previewTokens: {
    surfaceHover: PREVIEW_SURFACE_HOVER,
    borderStrong: PREVIEW_BORDER_STRONG
  }
})

// Light variant do preview — mesma identidade Conversacional WhatsApp,
// porém com fundo claro e contraste suave.
const PREVIEW_LIGHT_BG = '#f6f7f5'
const PREVIEW_LIGHT_SURFACE = '#ffffff'
const PREVIEW_LIGHT_SURFACE_HOVER = '#eef2ee'
const PREVIEW_LIGHT_BORDER = 'rgba(15, 23, 18, 0.08)'
const PREVIEW_LIGHT_BORDER_STRONG = 'rgba(15, 23, 18, 0.16)'
const PREVIEW_LIGHT_TEXT_PRIMARY = '#0b1f15'
const PREVIEW_LIGHT_TEXT_SECONDARY = '#4f5b54'

const themePreviewLight = createTheme({
  palette: {
    mode: 'light',
    textPrimary: PREVIEW_PRIMARY_DARK,
    background: {
      main: PREVIEW_LIGHT_BG,
      default: PREVIEW_LIGHT_BG,
      paper: PREVIEW_LIGHT_SURFACE
    },
    primary: {
      main: PREVIEW_PRIMARY_DARK,
      light: PREVIEW_PRIMARY,
      dark: '#16783b',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#2563eb',
      light: '#3b82f6',
      dark: '#1d4ed8',
      contrastText: '#ffffff'
    },
    success: { main: '#16a34a' },
    warning: { main: '#d97706' },
    error: { main: '#dc2626' },
    info: { main: '#2563eb' },
    divider: PREVIEW_LIGHT_BORDER,
    text: {
      primary: PREVIEW_LIGHT_TEXT_PRIMARY,
      secondary: PREVIEW_LIGHT_TEXT_SECONDARY
    }
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", Roboto, "Helvetica", Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    body1: { letterSpacing: '0.005em' },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em'
    }
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: PREVIEW_LIGHT_SURFACE,
          border: `1px solid ${PREVIEW_LIGHT_BORDER}`,
          transition: 'border-color 200ms ease, transform 200ms ease, box-shadow 200ms ease'
        },
        elevation2: {
          boxShadow: '0 1px 2px rgba(15,23,18,0.04), 0 4px 16px rgba(15,23,18,0.06)'
        },
        elevation3: {
          boxShadow: '0 2px 6px rgba(15,23,18,0.06), 0 12px 32px rgba(15,23,18,0.08)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
          textTransform: 'none',
          letterSpacing: '0.01em',
          paddingTop: 8,
          paddingBottom: 8
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: `0 6px 18px ${PREVIEW_PRIMARY_DARK}33`,
            transform: 'translateY(-1px)'
          }
        },
        outlined: {
          borderColor: PREVIEW_LIGHT_BORDER_STRONG,
          '&:hover': { borderColor: PREVIEW_PRIMARY_DARK }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, letterSpacing: '0.02em', borderRadius: 999 }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0f1112',
          color: '#f1f5f1',
          fontSize: 12,
          padding: '10px 14px',
          borderRadius: 10,
          boxShadow: '0 8px 24px rgba(15,23,18,0.18)'
        },
        arrow: { color: '#0f1112' }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: PREVIEW_LIGHT_BORDER_STRONG
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: `${PREVIEW_PRIMARY_DARK}80`
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: PREVIEW_PRIMARY_DARK,
            borderWidth: 2
          }
        }
      }
    },
    MuiAvatar: {
      styleOverrides: { root: { fontWeight: 600 } }
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: PREVIEW_LIGHT_BG },
        '@keyframes preview-pop': {
          '0%':   { opacity: 0, transform: 'translateY(10px) scale(0.98)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' }
        },
        '@keyframes preview-pulse-soft': {
          '0%, 100%': { boxShadow: `0 0 0 0 ${PREVIEW_PRIMARY_DARK}66` },
          '50%':      { boxShadow: `0 0 0 8px ${PREVIEW_PRIMARY_DARK}00` }
        },
        '.preview-card-enter': {
          animation: 'preview-pop 480ms cubic-bezier(0.22, 1, 0.36, 1) both'
        },
        '.preview-pulse': {
          animation: 'preview-pulse-soft 2s infinite'
        }
      }
    }
  },
  isDark: false,
  isPreview: true,
  previewTokens: {
    surfaceHover: PREVIEW_LIGHT_SURFACE_HOVER,
    borderStrong: PREVIEW_LIGHT_BORDER_STRONG
  }
})

const THEMES_BY_MODE = {
  light: themeLight,
  dark: themeDark,
  preview: themePreview,
  'preview-light': themePreviewLight
}

const ALLOWED_THEME_MODES = Object.keys(THEMES_BY_MODE)

const ThemeModeContext = createContext()

const ThemeModeProvider = ({ children }) => {
  const [queryParameters] = useSearchParams()
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
  const preferredTheme = window.localStorage.getItem('preferredTheme')
  const initialMode = ALLOWED_THEME_MODES.includes(preferredTheme)
    ? preferredTheme
    : (prefersDarkMode ? 'dark' : 'light')
  const [themeMode, setThemeMode] = useState(initialMode)
  const [theme, setTheme] = useState(THEMES_BY_MODE[initialMode] || themeDark)

  useEffect(() => {
    window.localStorage.setItem('preferredTheme', themeMode)
    setTheme(THEMES_BY_MODE[themeMode] || themeDark)
  }, [themeMode])

  useEffect(() => {
    const theme = queryParameters.get('theme')
    if (!ALLOWED_THEME_MODES.includes(theme)) return
    setThemeMode(theme)
  }, [queryParameters.get('theme')])

  return (
    <ThemeModeContext.Provider
      value={{
        theme,
        setTheme,
        themeMode,
        setThemeMode,
        availableModes: ALLOWED_THEME_MODES
      }}
    >
      {children}
    </ThemeModeContext.Provider>
  )
}

export {
  ThemeModeContext,
  ThemeModeProvider
}
